import { Link } from 'react-router-dom';

const LAST_UPDATED = 'February 27, 2026';
const COMPANY = 'Outlaw Oasis';
const SITE_URL = 'https://www.outlawoasis.com';
const CONTACT_EMAIL = 'legal@outlawoasis.com';

export default function Privacy() {
  return (
    <div className="py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-12 text-center">
          <h1 className="font-western text-4xl sm:text-5xl text-gold mb-4">Privacy Policy</h1>
          <p className="text-text-muted text-sm">Last updated: {LAST_UPDATED}</p>
        </div>

        <div className="space-y-10 text-text-secondary text-sm leading-relaxed">

          {/* Introduction */}
          <section>
            <p>
              {COMPANY} ("we", "us", or "our") is committed to protecting your privacy. This Privacy
              Policy explains what information we collect, how we use it, who we share it with, and what
              rights you have over your data when you use our platform at{' '}
              <span className="text-gold">{SITE_URL}</span>.
            </p>
            <p className="mt-4">
              By using our platform, you agree to the collection and use of information in accordance
              with this Privacy Policy. If you do not agree, please do not use our services.
            </p>
          </section>

          {/* 1 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              1. Information We Collect
            </h2>
            <div className="space-y-4">
              <div>
                <h3 className="text-cream font-medium mb-2">1.1 Information You Provide Directly</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>Account registration details (username, email address, password)</li>
                  <li>Profile information (display name, avatar, bio)</li>
                  <li>Payment information (processed and stored by our payment processor — we do not store full card details)</li>
                  <li>Communications with us (support tickets, messages)</li>
                  <li>Product listings created by sellers (descriptions, pricing, delivery information)</li>
                </ul>
              </div>
              <div>
                <h3 className="text-cream font-medium mb-2">1.2 Information Collected Automatically</h3>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>IP address and approximate geographic location</li>
                  <li>Browser type, device type, and operating system</li>
                  <li>Pages visited, time spent on pages, and navigation patterns</li>
                  <li>Referring URLs and search terms</li>
                  <li>Transaction history (products purchased or sold)</li>
                  <li>Session identifiers and authentication tokens</li>
                </ul>
              </div>
              <div>
                <h3 className="text-cream font-medium mb-2">1.3 Cookies &amp; Similar Technologies</h3>
                <p>
                  We use cookies and similar tracking technologies to maintain your session, remember your
                  preferences (such as your selected currency), and analyse platform usage. You may disable
                  cookies in your browser settings, but doing so may affect the functionality of our platform.
                </p>
              </div>
            </div>
          </section>

          {/* 2 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              2. How We Use Your Information
            </h2>
            <p>We use the information we collect to:</p>
            <ul className="list-disc list-inside mt-3 space-y-2 ml-2">
              <li>Create and maintain your account</li>
              <li>Process transactions and send related notifications</li>
              <li>Detect, investigate, and prevent fraud, abuse, and security incidents</li>
              <li>Verify seller identities and enforce our marketplace policies</li>
              <li>Provide customer support and respond to inquiries</li>
              <li>Improve and personalise our platform (e.g., currency detection by IP)</li>
              <li>Send transactional emails (order confirmations, password resets, support replies)</li>
              <li>Send marketing communications, where you have opted in to receive them</li>
              <li>Comply with applicable legal obligations</li>
            </ul>
          </section>

          {/* 3 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              3. How We Share Your Information
            </h2>
            <p>We do not sell your personal information. We may share your data with:</p>
            <div className="mt-4 space-y-4">
              <div>
                <h3 className="text-cream font-medium mb-1">3.1 Payment Processors</h3>
                <p>
                  We use third-party payment processors (such as Stripe) to handle payment transactions.
                  These processors receive the payment information necessary to complete your transaction and
                  are governed by their own privacy policies. We encourage you to review those policies.
                </p>
              </div>
              <div>
                <h3 className="text-cream font-medium mb-1">3.2 Between Buyers and Sellers</h3>
                <p>
                  When a transaction is completed, limited information necessary to fulfil the order (such
                  as your username) may be shared with the counterparty. We do not share your full contact
                  details or payment information with other users.
                </p>
              </div>
              <div>
                <h3 className="text-cream font-medium mb-1">3.3 Service Providers</h3>
                <p>
                  We may engage trusted third-party service providers to assist with hosting, analytics,
                  email delivery, and fraud prevention. These providers are contractually obligated to
                  handle your data securely and only for the purposes we specify.
                </p>
              </div>
              <div>
                <h3 className="text-cream font-medium mb-1">3.4 Legal Requirements</h3>
                <p>
                  We may disclose your information if required to do so by law, regulation, legal process,
                  or governmental request, or if we believe in good faith that such disclosure is necessary
                  to protect the rights, property, or safety of {COMPANY}, our users, or the public.
                </p>
              </div>
              <div>
                <h3 className="text-cream font-medium mb-1">3.5 Business Transfers</h3>
                <p>
                  If {COMPANY} is involved in a merger, acquisition, or sale of all or a portion of its
                  assets, your information may be transferred as part of that transaction. We will notify
                  you via email and/or a prominent notice on our platform of any such change in ownership.
                </p>
              </div>
            </div>
          </section>

          {/* 4 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              4. Data Security
            </h2>
            <p>
              We implement industry-standard security measures to protect your personal information,
              including encryption of data in transit (TLS/HTTPS), hashed storage of passwords, and
              access controls limiting who can view your data. However, no method of transmission over
              the internet or electronic storage is 100% secure. We cannot guarantee absolute security
              and encourage you to use a strong, unique password for your account.
            </p>
            <p className="mt-3">
              If we become aware of a data breach that affects your personal information, we will notify
              you as required by applicable law.
            </p>
          </section>

          {/* 5 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              5. Data Retention
            </h2>
            <p>
              We retain your personal information for as long as your account is active or as needed to
              provide our services, comply with our legal obligations, resolve disputes, and enforce our
              agreements. When you delete your account, we will delete or anonymise your personal data
              within a reasonable period, except where retention is required by law.
            </p>
            <p className="mt-3">
              Transaction records may be retained for longer periods as required for tax, financial, and
              fraud prevention purposes.
            </p>
          </section>

          {/* 6 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              6. Your Rights &amp; Choices
            </h2>
            <p>
              Depending on your location, you may have the following rights regarding your personal
              information:
            </p>
            <ul className="list-disc list-inside mt-3 space-y-2 ml-2">
              <li>
                <strong className="text-cream">Access:</strong> Request a copy of the personal data we hold about you.
              </li>
              <li>
                <strong className="text-cream">Correction:</strong> Request that we correct inaccurate or incomplete data.
              </li>
              <li>
                <strong className="text-cream">Deletion:</strong> Request deletion of your personal data, subject to legal retention requirements.
              </li>
              <li>
                <strong className="text-cream">Portability:</strong> Request your data in a machine-readable format.
              </li>
              <li>
                <strong className="text-cream">Objection:</strong> Object to certain types of processing, including direct marketing.
              </li>
              <li>
                <strong className="text-cream">Withdraw Consent:</strong> Where processing is based on consent, withdraw it at any time without affecting the lawfulness of prior processing.
              </li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, please contact us at{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className="text-gold hover:underline">
                {CONTACT_EMAIL}
              </a>. We will respond within the timeframe required by applicable law.
            </p>
          </section>

          {/* 7 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              7. Children's Privacy
            </h2>
            <p>
              Our platform is not directed to individuals under the age of 18. We do not knowingly collect
              personal information from minors. If we become aware that we have collected data from a person
              under 18 without verifiable parental consent, we will take steps to delete that information
              as soon as possible. If you believe a minor has provided us with their personal data, please
              contact us immediately.
            </p>
          </section>

          {/* 8 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              8. International Data Transfers
            </h2>
            <p>
              {COMPANY} operates globally. Your information may be transferred to and processed in countries
              other than your country of residence. These countries may have different data protection laws
              than your own. By using our platform, you consent to such transfers. Where required, we take
              appropriate safeguards (such as standard contractual clauses) to ensure your data is protected
              in accordance with this Privacy Policy.
            </p>
          </section>

          {/* 9 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              9. Third-Party Links
            </h2>
            <p>
              Our platform may contain links to third-party websites, including game publisher sites and
              external forums. This Privacy Policy does not apply to those websites. We encourage you to
              review the privacy policies of any third-party site you visit. We are not responsible for
              the privacy practices of third parties.
            </p>
          </section>

          {/* 10 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              10. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. When we make material changes, we will
              update the "Last updated" date at the top of this page and, where appropriate, notify you
              by email or by a notice on our platform. Your continued use of our services after any changes
              constitutes your acceptance of the updated policy.
            </p>
          </section>

          {/* 11 */}
          <section>
            <h2 className="text-xl font-semibold text-cream mb-3 border-b border-gold/20 pb-2">
              11. Contact Us
            </h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data
              practices, please contact us at:
            </p>
            <div className="mt-3 p-4 bg-leather rounded-lg border border-gold/20">
              <p className="text-cream font-semibold">{COMPANY}</p>
              <p className="mt-1">
                Email:{' '}
                <a href={`mailto:${CONTACT_EMAIL}`} className="text-gold hover:underline">
                  {CONTACT_EMAIL}
                </a>
              </p>
              <p>
                Website:{' '}
                <a href={SITE_URL} className="text-gold hover:underline">
                  {SITE_URL}
                </a>
              </p>
            </div>
          </section>

          {/* Related links */}
          <div className="pt-6 border-t border-gold/10 flex flex-wrap gap-4 text-sm">
            <Link to="/terms" className="text-gold hover:underline">Terms of Service</Link>
            <Link to="/refunds" className="text-gold hover:underline">Refund Policy</Link>
            <Link to="/support" className="text-gold hover:underline">Contact Support</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
